import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MetricCard } from '@repo/components';
import { useUserPreferences } from 'src/contexts/UserPreferenceContext';
import { getSiteOverview } from 'src/api/admin/sites';
import { ComplianceSection } from 'src/parts/overview/ComplianceSection';
import { DailyUsersChart } from 'src/parts/overview/DailyUsersChart';
import { EventOverviewSection } from 'src/parts/overview/EventOverviewSection';
import { Inhoudingsplicht } from 'src/parts/overview/Inhoudingsplicht';
import { OverviewSkeleton } from 'src/parts/overview/OverviewSkeleton';
import { PERMISSIONS } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/_admin/$siteId/overview/')({
	component: OverviewPage,
});

function OverviewPage() {
	const { siteId } = Route.useParams();
	const { user: authenticatedUser } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const { data: siteOverviewData, isLoading } = useQuery({
		queryKey: ['site-overview', siteId],
		queryFn: () => getSiteOverview(siteId),
	});

	useBreadcrumbLevel(0, siteOverviewData?.site?.name || '');
	if (isLoading || !siteOverviewData) {
		return <OverviewSkeleton />;
	}

	return (
		<Suspense fallback={<OverviewSkeleton />}>
			<OverviewContent
				data={siteOverviewData}
				hasAdminEventsViewPermission={verifyPermission(PERMISSIONS.adminEventsView)}
			/>
		</Suspense>
	);
}

interface OverviewContentProps {
	data: Awaited<ReturnType<typeof getSiteOverview>>;
	hasAdminEventsViewPermission: boolean;
}

function OverviewContent({ data, hasAdminEventsViewPermission }: OverviewContentProps) {
	const { siteId, stats, compliancy, overviewEvents, dailyUsersData, inhoudingsplichtOverview } = data;
	const { userPreferences } = useUserPreferences();
	const visualSettings = userPreferences.visualSettings;
	const navigate = useNavigate();

	const navigateTo = (path: string) => navigate({ to: path });

	const visibleSections = [
		visualSettings?.complianceSection,
		visualSettings?.eventOverviewSection,
		visualSettings?.inhoudingsplicht,
	].filter(Boolean).length;

	const getGridCols = () => {
		if (visibleSections === 1) return 'grid-cols-2';
		if (visibleSections === 2) return 'grid-cols-1 lg:grid-cols-2';
		if (visibleSections === 3) return 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3';
		return 'grid-cols-1';
	};

	return (
		<div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
			{visualSettings?.userMetrics && (
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[140px]">
					<MetricCard
						title="Users - On Site"
						count={stats.currentlyCheckedIn}
						variant="success"
						onClick={() => navigateTo(`/${siteId}/overview/attendance`)}
						tooltip="Count of current checked in Users"
					/>
					<MetricCard
						title="Users - Checked in today"
						count={stats.checkedInToday}
						variant="dark"
						onClick={() => navigateTo(`/${siteId}/overview/attendance`)}
						tooltip="Count of checked in Users for today"
					/>
					<MetricCard
						title="Users - Checked in last hour"
						count={stats.checkedInLastHour}
						variant="warning"
						onClick={() => navigateTo(`/${siteId}/overview/attendance`)}
						tooltip="Count of checked in Users during last hour"
					/>
				</div>
			)}

			{visualSettings?.dailyUsersChart && (
				<DailyUsersChart data={dailyUsersData} currentCheckins={stats.currentlyCheckedIn} siteId={siteId} />
			)}

			{visibleSections > 0 && (
				<div className={`grid gap-6 ${getGridCols()}`}>
					{visualSettings?.complianceSection && <ComplianceSection data={compliancy} />}
					{visualSettings?.eventOverviewSection && (
						<EventOverviewSection
							overviewEvents={overviewEvents}
							hasAdminEventsViewPermission={hasAdminEventsViewPermission}
						/>
					)}
					{visualSettings?.inhoudingsplicht && <Inhoudingsplicht data={inhoudingsplichtOverview} />}
				</div>
			)}
		</div>
	);
}
